import React from 'react';
import { DialogFooter, DialogHeader, DialogTitle } from '../../../../common/components/ui/dialog';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useDeliveryCalendar } from '../../routes/resources/CartDeliveryCalendarRoute';
import type { ReactElement } from 'react';
import TgsCalendar from '../../../../common/components/calendar/TgsCalendar';
import { CircularProgress } from '../../../../common/components/CircularProgress';
import { Button } from '../../../../common/components/ui/button';
import { ScrollArea } from '../../../../common/components/ui/scroll-area';

export function CartDialogDeliveryCalendar({
  title,
  description,
  children
}: Readonly<{
  title: string;
  description?: string;
  children: ReactElement;
}>) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className={'flex items-center'}>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>

      <CartDialogDeliveryCalendarContent>{children}</CartDialogDeliveryCalendarContent>
    </>
  );
}

function CartDialogDeliveryCalendarContent({ children }: Readonly<{ children: React.ReactElement }>) {
  const { t } = useTranslation();
  const {
    deliveryDateWarning,
    calendarData,
    selectedDate,
    nextMonth,
    previousMonth,
    onDateSelected,
    confirmSundayWarning,
    abortSundayWarning,
    isLoading,
    cartIsChangingDeliveryDate,
    cartIsChangingDeliveryDateExpired,
    reset
  } = useDeliveryCalendar();

  if (isLoading || !calendarData) {
    return (
      <div className="flex justify-center items-center min-h-[448px]">
        <CircularProgress size={'lg'} />
      </div>
    );
  }

  if (cartIsChangingDeliveryDate || cartIsChangingDeliveryDateExpired) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[448px]">
        <CircularProgress size={'lg'} className={'mb-4'} />
        <p>{t('Wir verschieben gerade alle Positionen auf das neue Datum.')}</p>
        <p>{t('Dieser Vorgang kann etwas dauern.')}</p>
      </div>
    );
  }

  if (deliveryDateWarning) {
    return (
      <div className={'justify-center flex'}>
        <div className={'flex flex-col gap-4 p-2'}>
          <h2 className="text-2xl font-bold">{deliveryDateWarning.warningTitle}</h2>
          <p className="text-base">{deliveryDateWarning.warningText}</p>

          <div className="flex justify-end space-x-4">
            <Button
              variant={'primaryOutline'}
              onClick={abortSundayWarning}
              id="calendar-date-warning-abort"
              data-testid={'cart-new-warning-abort-button'}
            >
              {t('Abbrechen')}
            </Button>
            <Button
              onClick={confirmSundayWarning}
              id="calendar-date-warning-confirm"
              data-testid={'cart-new-warning-ok-button'}
            >
              {t('Ok')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="overflow-y-auto">
        <TgsCalendar
          isReadonly={false}
          deliveryCalendar={calendarData}
          selectedDate={selectedDate}
          onDateSelected={onDateSelected}
          onNextMonth={nextMonth}
          onPreviousMonth={previousMonth}
          reset={reset}
        />
      </ScrollArea>
      <DialogFooter className={'flex-col desktop:flex-row gap-4 desktop:gap-1'}>{children}</DialogFooter>
    </>
  );
}
